import { CommonModule } from '@angular/common'
import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { ReactiveFormsModule, Validators } from '@angular/forms'
import {
  E11DynamicFormBuilderComponent,
  E11DynamicFormModule,
  e11FieldInputMultiSelector,
  e11FieldInputSelect,
  e11FieldInputSwitch,
  e11FormFor,
  E11FormForType,
  e11FormLayoutFullWidth,
  e11LayoutGroup,
  IFormTemplateSpec,
} from '@engineering11/ui-lib/e11-form-builder'
import { ISelectOption } from '@engineering11/ui-lib/e11-select'
import { E11ShowHideWrapperComponent } from '@engineering11/ui-lib/e11-show-hide'
import { TranslateModule } from '@ngx-translate/core'
import { APPLICATION_STATE, SharedLibModule } from 'shared-lib'
import { IAutomationFormConfig } from '../automation-form-message'
import { AutomationFormHeaderComponent } from '../partials/automation-form-header.component'
import { AutomationReminderMessagesComponent } from '../partials/automation-reminder-messages.component'
import { AutomationRequestedUpdateHelperComponent } from '../partials/automation-requested-update-helper.component'
import { AutomationFormData, AutomationResetComponent } from '../partials/automation-reset.component'
import { AutomationTypeDisplayNames } from '../partials/automation-upgrade-prompt.component'
import {
  IRequestMoreFormData,
  IRequestUpdatesFormData,
  requestedFieldOptions,
  requestedUpdateDefaults,
  requestMoreFormDefaults,
} from './auto-request-updates.model'
import { deepNormalizeNullishStrings } from '@engineering11/utility'

@Component({
  selector: 'automation-request-updates-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    E11DynamicFormModule,
    E11ShowHideWrapperComponent,
    TranslateModule,
    AutomationFormHeaderComponent,
    AutomationResetComponent,
    AutomationRequestedUpdateHelperComponent,
    AutomationReminderMessagesComponent,
    SharedLibModule,
  ],
  template: `
    <div>
      <automation-form-header
        [sectionTitle]="'Request Updates'"
        [ottoHelperText]="
          requestUpdatesForm?.form?.get('newSwimlane')?.value
            ? 'You currently have an automated swimlane change from Applied to ' +
              (requestUpdatesForm?.form?.get('newSwimlane')?.value | applicationState) +
              ' once a candidate completes a Request for More task.'
            : 'You do not currently have a swimlane change configured for this automation task.'
        "
        [showEnableSwitch]="showEnableSwitch"
        [isCollapsed]="isCollapsed"
        [isEnabled]="requestUpdatesForm?.form?.get('enabled')?.value ?? false"
        (enabledChanged)="requestUpdatesForm?.form?.get('enabled')?.setValue($event)"
        (isCollapsedChanged)="isCollapsed = $event"
      ></automation-form-header>

      <p>
        {{
          'Choose the parts of your applications you find important and let the system automatically ask the candidate to provide more information if these areas are left blank.'
            | translate
        }}
        <job-automation-requested-update-helper></job-automation-requested-update-helper>
      </p>

      <e11-show-hide-wrapper [isCollapsed]="isCollapsed">
        @if (fieldGroups) {
        <div class="e11-flex e11-flex-col e11-gap-16">
          <e11-dynamic-form-builder
            #requestUpdatesForm
            [loading]="loading"
            [fieldGroups]="fieldGroups"
            [initialValue]="initialData.requestedUpdate"
            [hideSubmitMenu]="true"
          >
            <ng-template e11Template="requestMoreInfo">
              <div class="">
                <div class="e11-font-bold">{{ 'What to check' | translate }}</div>
                <p>{{ 'If these areas of the application are left blank we will ask the candidate to supply information.' | translate }}</p>
              </div>
            </ng-template>
            <ng-template e11Template="swimlaneInfo">
              <div class="">
                <div class="e11-font-bold">{{ 'Where to go next' | translate }}</div>
                <p>{{ 'After the candidate completes these requests where do you want to place the candidate in your pipeline?' | translate }}</p>
              </div>
            </ng-template>
            <ng-template e11Template="resetFormTemplate">
              <automation-reset
                *ngIf="showResetButton"
                [formField]="'requestedUpdate'"
                [loading]="loading"
                (formFieldReset)="formFieldReset($event)"
              ></automation-reset>
            </ng-template>
          </e11-dynamic-form-builder>

          <automation-reminder-messages
            #reminderMessagesRef
            [automationTypeDisplayName]="automationTypeDisplayName"
            [loading]="loading"
            [showResetButton]="showResetButton"
            [formConfig]="formConfig"
            [showImmediateSwitch]="true"
            [immediateValue]="initialData.immediate"
            [initialMessageConfig]="{
              data: initialData.initialMessage,
              fieldName: 'requestMoreInitial',
              showEnableSwitch: showEnableSwitch,
              footNoteText:
                'Sent to the candidates after the application is submitted, reviewed and the sections listed have been determined to be lacking any data.'
            }"
            [followUpMessageConfig]="{
              data: initialData.followUp,
              fieldName: 'requestMoreFollowUp',
              showEnableSwitch: showEnableSwitch
            }"
            [warningMessageConfig]="{
              data: initialData.warning,
              fieldName: 'requestMoreWarning',
              showEnableSwitch: showEnableSwitch
            }"
          ></automation-reminder-messages>
        </div>
        }
      </e11-show-hide-wrapper>
    </div>
  `,
})
export class AutomationRequestUpdatesSectionComponent implements OnInit {
  @ViewChild('requestUpdatesForm') requestUpdatesForm!: E11DynamicFormBuilderComponent<IRequestUpdatesFormData>
  @ViewChild('reminderMessagesRef') reminderMessagesRef!: AutomationReminderMessagesComponent
  @Input() loading = false
  @Input() showEnableSwitch = false
  @Input() showResetButton = false
  @Input() formConfig: IAutomationFormConfig | undefined
  @Input() initialData: IRequestMoreFormData = requestMoreFormDefaults
  @Input({ required: true }) swimlaneOptions!: ISelectOption<APPLICATION_STATE | null>[]

  isCollapsed = false
  automationTypeDisplayName = AutomationTypeDisplayNames.RequestedUpdate
  fieldGroups?: E11FormForType<IRequestUpdatesFormData>

  ngOnInit() {
    this.buildForms()
  }

  private buildForms() {
    if (!this.formConfig) {
      return
    }

    this.fieldGroups = this.buildRequestedUpdateForm(this.formConfig)
  }

  private buildRequestedUpdateForm(config: IAutomationFormConfig): E11FormForType<IRequestUpdatesFormData> {
    const { enabled, fields, newSwimlane } = this.initialData?.requestedUpdate ?? requestedUpdateDefaults
    const { canEditMessages } = config
    const premiumTemplates: IFormTemplateSpec[] = canEditMessages
      ? [
          {
            templateKey: 'resetFormTemplate',
            templatePosition: 'bottom',
          },
        ]
      : []

    return e11FormFor<IRequestUpdatesFormData>()([
      e11FormLayoutFullWidth(
        [
          e11FieldInputSwitch('enabled', 'Enabled', enabled, {
            validatorOrOpts: [],
          }),
        ],
        { containerClass: 'e11-hidden' }
      ),
      e11LayoutGroup(
        [
          e11LayoutGroup([
            e11FormLayoutFullWidth(
              [
                e11FieldInputMultiSelector('fields', '', requestedFieldOptions, fields, {
                  validatorOrOpts: [Validators.required],
                }),
              ],
              { templateSpec: { templateKey: 'requestMoreInfo', templatePosition: 'top' } }
            ),
          ]),
          e11LayoutGroup([
            e11FormLayoutFullWidth(
              [
                e11FieldInputSelect('newSwimlane', 'New Swimlane', this.swimlaneOptions, newSwimlane, {
                  validatorOrOpts: [],
                  extraInputOption: {
                    required: true,
                  },
                }),
              ],
              { templateSpec: { templateKey: 'swimlaneInfo', templatePosition: 'top' } }
            ),
          ]),
        ],
        {
          layoutContainerClass: 'e11-grid e11-grid-cols-1 md:e11-grid-cols-2 e11-gap-4',
          templateSpec: premiumTemplates,
        }
      ),
    ])
  }

  formFieldReset(data: AutomationFormData) {
    this.requestUpdatesForm.reset(data as IRequestUpdatesFormData)
  }

  get value(): IRequestMoreFormData {
    return {
      immediate: this.reminderMessagesRef?.getValue()?.immediate ?? false,
      requestedUpdate: deepNormalizeNullishStrings(this.requestUpdatesForm.form.value),
      initialMessage: this.reminderMessagesRef?.getValue()?.initialMessage || this.initialData?.initialMessage,
      followUp: this.reminderMessagesRef?.getValue()?.followUp || this.initialData?.followUp,
      warning: this.reminderMessagesRef?.getValue()?.warning || this.initialData?.warning,
    }
  }

  validate(): boolean {
    this.requestUpdatesForm.onSubmitClick()
    if (!this.requestUpdatesForm.form.valid) {
      return false
    }

    if (this.reminderMessagesRef && !this.reminderMessagesRef.validate()) {
      return false
    }

    return true
  }
}
